import { createRouter, createWebHistory } from 'vue-router'
import { requireAuth, requireRole, requireGuest } from './guards'
import { useAuthStore } from '@/stores/auth'
import { authApi } from '@/api/auth'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'welcome',
      component: () => import('@/pages/Welcome.vue'),
      meta: {
        requiresAuth: false,
        title: 'Welcome',
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/auth/Login.vue'),
      beforeEnter: requireGuest,
      meta: {
        requiresAuth: false,
        roles: ['customer'],
        title: 'Customer Login',
      },
    },
    {
      path: '/masuk',
      name: 'admin-login',
      component: () => import('@/pages/auth/AdminLogin.vue'),
      beforeEnter: requireGuest,
      meta: {
        requiresAuth: false,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Admin Login',
      },
    },
    {
      path: '/branch/access',
      name: 'staff-login',
      component: () => import('@/pages/auth/StaffLogin.vue'),
      beforeEnter: requireGuest,
      meta: {
        requiresAuth: false,
        roles: ['staff', 'branch-manager'],
        title: 'Branch Access',
      },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/pages/auth/Register.vue'),
      beforeEnter: requireGuest,
      meta: {
        requiresAuth: false,
        title: 'Register',
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/pages/auth/ForgotPassword.vue'),
      beforeEnter: requireGuest,
      meta: {
        requiresAuth: false,
        title: 'Forgot Password',
      },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/pages/auth/ResetPassword.vue'),
      beforeEnter: requireGuest,
      meta: {
        requiresAuth: false,
        title: 'Reset Password',
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/pages/dashboard/Index.vue'),
      beforeEnter: requireAuth,
      meta: {
        requiresAuth: true,
        title: 'Dashboard',
      },
    },
    // Customer routes
    {
      path: '/dashboard/my-orders',
      name: 'my-orders',
      component: () => import('@/pages/dashboard/orders/Index.vue'),
      beforeEnter: requireRole('customer'),
      meta: {
        requiresAuth: true,
        roles: ['customer'],
        title: 'My Orders',
      },
    },
    {
      path: '/dashboard/my-orders/:orderNumber',
      name: 'order-detail',
      component: () => import('@/pages/dashboard/orders/Show.vue'),
      beforeEnter: requireRole('customer'),
      meta: {
        requiresAuth: true,
        roles: ['customer'],
        title: 'Order Details',
      },
    },
    {
      path: '/dashboard/my-addresses',
      name: 'my-addresses',
      component: () => import('@/pages/dashboard/addresses/Index.vue'),
      beforeEnter: requireRole('customer'),
      meta: {
        requiresAuth: true,
        roles: ['customer'],
        title: 'My Addresses',
      },
    },
    {
      path: '/dashboard/my-addresses/create',
      name: 'address-create',
      component: () => import('@/pages/dashboard/addresses/Form.vue'),
      beforeEnter: requireRole('customer'),
      meta: {
        requiresAuth: true,
        roles: ['customer'],
        title: 'Add Address',
      },
    },
    {
      path: '/dashboard/my-addresses/:id/edit',
      name: 'address-edit',
      component: () => import('@/pages/dashboard/addresses/Form.vue'),
      beforeEnter: requireRole('customer'),
      meta: {
        requiresAuth: true,
        roles: ['customer'],
        title: 'Edit Address',
      },
    },
    {
      path: '/dashboard/wallet',
      name: 'wallet',
      component: () => import('@/pages/dashboard/Wallet.vue'),
      beforeEnter: requireRole('customer'),
      meta: {
        requiresAuth: true,
        roles: ['customer'],
        title: 'Wallet',
      },
    },
    {
      path: '/dashboard/profile',
      name: 'profile',
      component: () => import('@/pages/dashboard/Profile.vue'),
      beforeEnter: requireAuth,
      meta: {
        requiresAuth: true,
        title: 'My Profile',
      },
    },
    // Public service detail page
    {
      path: '/services/:id',
      name: 'service-detail',
      component: () => import('@/pages/services/Detail.vue'),
      meta: {
        requiresAuth: false,
        title: 'Service Details',
      },
    },
    // Public pages (Privacy Policy, Terms, etc.)
    {
      path: '/page/:slug',
      name: 'page',
      component: () => import('@/pages/Page.vue'),
      meta: {
        requiresAuth: false,
        title: 'Page',
      },
    },
    // Public booking flow
    {
      path: '/book',
      name: 'booking-flow',
      component: () => import('@/pages/booking/Flow.vue'),
      meta: {
        requiresAuth: false,
        title: 'Book a Service',
      },
    },
    {
      path: '/booking/:order',
      name: 'booking-status',
      component: () => import('@/pages/booking/Status.vue'),
      meta: {
        requiresAuth: false,
        title: 'Booking Status',
      },
    },
    // Admin routes
    {
      path: '/dashboard/orders',
      name: 'admin-orders',
      component: () => import('@/pages/admin/orders/Index.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher', 'branch-manager'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher', 'branch-manager'],
        title: 'Orders Management',
      },
    },
    {
      path: '/dashboard/orders/:id',
      name: 'admin-order-detail',
      component: () => import('@/pages/admin/orders/Show.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher', 'branch-manager'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher', 'branch-manager'],
        title: 'Order Details',
      },
    },
    {
      path: '/dashboard/branches',
      name: 'admin-branches',
      component: () => import('@/pages/admin/branches/Index.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Branches',
      },
    },
    {
      path: '/dashboard/branches/create',
      name: 'branch-create',
      component: () => import('@/pages/admin/branches/Form.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Add Branch',
      },
    },
    {
      path: '/dashboard/branches/:id/edit',
      name: 'branch-edit',
      component: () => import('@/pages/admin/branches/Form.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Edit Branch',
      },
    },
    {
      path: '/dashboard/services',
      name: 'admin-services',
      component: () => import('@/pages/admin/services/Index.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Services',
      },
    },
    {
      path: '/dashboard/services/create',
      name: 'service-create',
      component: () => import('@/pages/admin/services/Form.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Add Service',
      },
    },
    {
      path: '/dashboard/services/:id/edit',
      name: 'service-edit',
      component: () => import('@/pages/admin/services/Form.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Edit Service',
      },
    },
    {
      path: '/dashboard/categories',
      name: 'admin-categories',
      component: () => import('@/pages/admin/categories/Index.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Categories',
      },
    },
    {
      path: '/dashboard/categories/create',
      name: 'category-create',
      component: () => import('@/pages/admin/categories/Form.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Add Category',
      },
    },
    {
      path: '/dashboard/categories/:id/edit',
      name: 'category-edit',
      component: () => import('@/pages/admin/categories/Form.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Edit Category',
      },
    },
    {
      path: '/dashboard/promotions',
      name: 'admin-promotions',
      component: () => import('@/pages/admin/promotions/Index.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Promotions',
      },
    },
    {
      path: '/dashboard/pages',
      name: 'admin-pages',
      component: () => import('@/pages/admin/pages/Index.vue'),
      beforeEnter: requireRole('hq-admin'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin'],
        title: 'Pages',
      },
    },
    {
      path: '/dashboard/pages/create',
      name: 'page-create',
      component: () => import('@/pages/admin/pages/Form.vue'),
      beforeEnter: requireRole('hq-admin'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin'],
        title: 'Add Page',
      },
    },
    {
      path: '/dashboard/pages/:id/edit',
      name: 'page-edit',
      component: () => import('@/pages/admin/pages/Form.vue'),
      beforeEnter: requireRole('hq-admin'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin'],
        title: 'Edit Page',
      },
    },
    {
      path: '/dashboard/settings',
      name: 'admin-settings',
      component: () => import('@/pages/admin/Settings.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Settings',
      },
    },
    {
      path: '/dashboard/staff',
      name: 'admin-staff',
      component: () => import('@/pages/admin/staff/Index.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher', 'branch-manager'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher', 'branch-manager'],
        title: 'Staff',
      },
    },
    {
      path: '/dashboard/reports',
      name: 'admin-reports',
      component: () => import('@/pages/admin/Reports.vue'),
      beforeEnter: requireRole('hq-admin', 'hq-dispatcher'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin', 'hq-dispatcher'],
        title: 'Reports',
      },
    },
    {
      path: '/dashboard/reports/transactions',
      name: 'admin-transaction-report',
      component: () => import('@/pages/admin/reports/Transactions.vue'),
      beforeEnter: requireRole('hq-admin'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin'],
        title: 'Transaction Report',
      },
    },
    {
      path: '/dashboard/reports/commissions',
      name: 'admin-commission-report',
      component: () => import('@/pages/admin/reports/Commissions.vue'),
      beforeEnter: requireRole('hq-admin'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin'],
        title: 'Commission Report',
      },
    },
    // Customer Management (HQ Admin only)
    {
      path: '/dashboard/customers',
      name: 'admin-customers',
      component: () => import('@/pages/admin/customers/Index.vue'),
      beforeEnter: requireRole('hq-admin'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin'],
        title: 'Customers',
      },
    },
    {
      path: '/dashboard/customers/:id',
      name: 'admin-customer-detail',
      component: () => import('@/pages/admin/customers/Show.vue'),
      beforeEnter: requireRole('hq-admin'),
      meta: {
        requiresAuth: true,
        roles: ['hq-admin'],
        title: 'Customer Details',
      },
    },
    // Staff routes
    {
      path: '/dashboard/jobs',
      name: 'staff-jobs',
      component: () => import('@/pages/staff/Jobs.vue'),
      beforeEnter: requireRole('staff'),
      meta: {
        requiresAuth: true,
        roles: ['staff'],
        title: 'My Jobs',
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/NotFound.vue'),
      meta: {
        requiresAuth: false,
        title: 'Page Not Found',
      },
    },
  ],
})

// Global navigation guard to check authentication on route changes
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // If user has token but no user data, try to fetch user
  if (authStore.token && !authStore.user) {
    try {
      const response = await authApi.me()
      authStore.setAuth(response.data.user, authStore.token)
    } catch (error) {
      // Token is invalid, clear it
      authStore.clearAuth()
    }
  }
  
  // Update document title from route meta
  if (to.meta?.title) {
    document.title = `${to.meta.title} - MySettle`
  }
  
  next()
})

export default router

